import api from "./http";
import { generateHeaders, generateHeadersByKeyAndSecret } from "./sign";

/**
 * 执行SQL查询
 * @param {string} sql - 要执行的SQL语句
 * @returns {Promise<Object>} 返回API响应数据
 */
export async function executeSql(sql) {
  try {
    const headers = await generateHeaders("weinxqlhhktop");
    const body = {
      method: "api.wei.nxqlhhk.template",
      requestBody: { sql: sql },
    };
    const response = await api.post(
      "/dw/admin/api/tracking/route/rest",
      body,
      headers,
    );
    if (response.code !== 200) {
      throw new Error(
        `请求失败，状态码: ${response.code},错误信息: ${response.errorMsg}`,
      );
    }
    const result = response.data ? response.data : {};
    return result;
  } catch (error) {
    console.error("执行SQL失败:", error);
    throw error;
  }
}

export async function debugRestApi(method, requestBody, appKey, appSecret) {
  try {
    const headers = await generateHeadersByKeyAndSecret(appKey, appSecret);
    const body = {
      method: method,
      requestBody: requestBody,
    };
    const response = await api.post(
      "/dw/admin/api/tracking/route/rest",
      body,
      headers,
    );
    if (response.code !== 200) {
      throw new Error(
        `调试接口失败，状态码: ${response.code},错误信息: ${response.errorMsg}`,
      );
    }
    const result = response.data ? response.data : {};
    return result;
  } catch (error) {
    console.error("调试接口失败:", error);
    throw error;
  }
}

function escapeContent(content) {
  if (!content) return content;

  // 1. 转为字符串（避免传入对象）
  let contentStr =
    typeof content === "string" ? content : JSON.stringify(content);

  // 2. 替换 #{xxx} → \#{xxx}
  contentStr = contentStr.replace(/#\{(\w+)\}/g, (_, key) => `\\#{${key}}`);

  // 3. 替换单引号 ' → \'
  contentStr = contentStr.replace(/'/g, "\\'");

  return contentStr;
}

/**
 * 获取任务列表
 * @returns {Promise<Array>} 返回任务列表数组
 */
export async function getTaskList() {
  const sql = "SELECT * FROM automatic_report.`t_task_job` ORDER BY id DESC";
  return executeSql(sql);
}

/**
 * 获取模板列表
 * @returns {Promise<Array>} 返回模板列表数组
 */
export async function getTemplateList() {
  const sql =
    "SELECT id,title FROM automatic_report.`t_template` ORDER BY id DESC";
  return executeSql(sql);
}

/**
 * 获取appKey列表
 * @returns {Promise<Array>} 返回appKey列表数组
 */
export async function getAppKeyList() {
  const sql =
    "SELECT id, login_name AS appKey, password AS appSecret FROM automatic_report.t_sys_user ORDER BY id DESC";
  return executeSql(sql);
}

/**
 * 获取转发Api列表
 * @returns {Promise<Array>} 返回转发Api列表数组
 */
export async function getForwardApiList() {
  const sql =
    "SELECT id, label, title, method, type, content, add_time FROM automatic_report.t_api_config ORDER BY id DESC";
  return executeSql(sql);
}

/**
 * 创建任务
 * @param {Object} taskData - 任务数据
 * @returns {Promise<Object>} 返回创建结果
 */
export async function createTask(taskData) {
  const { title, type, cron_cycle_type, start_date, end_date, content } =
    taskData;
  const sql = `INSERT INTO t_task_job (title, type, cron_cycle_type, start_date, end_date, content) VALUES ('${title}', '${type}', '${cron_cycle_type}', '${start_date}', '${end_date}', '${JSON.stringify(
    content,
  )}')`;
  return executeSql(sql);
}

/**
 * 更新任务
 * @param {number} id - 任务ID
 * @param {Object} taskData - 任务数据
 * @returns {Promise<Object>} 返回更新结果
 */
export async function updateTask(id, taskData) {
  const { title, type, cron_cycle_type, start_date, end_date, content } =
    taskData;
  const sql = `UPDATE t_task_job SET title='${title}', type='${type}', cron_cycle_type='${cron_cycle_type}', start_date='${start_date}', end_date='${end_date}', content='${JSON.stringify(
    content,
  )}' WHERE id=${id}`;
  return executeSql(sql);
}

/**
 * 删除任务
 * @param {number} id - 任务ID
 * @returns {Promise<Object>} 返回删除结果
 */
export async function deleteTask(id) {
  const sql = `DELETE FROM t_task_job WHERE id=${id}`;
  return executeSql(sql);
}

/**
 * 开启任务
 * @param {number} id - 任务ID
 * @returns {Promise<Object>} 返回开启结果
 */
export async function startTask(id) {
  const sql = `UPDATE t_task_job SET end_date=DATE_ADD(CURDATE(), INTERVAL 1 YEAR), status='' WHERE id=${id} AND (start_date < CURDATE() OR start_date = CURDATE())`;
  return executeSql(sql);
}

/**
 * 关闭任务
 * @param {number} id - 任务ID
 * @returns {Promise<Object>} 返回关闭结果
 */
export async function stopTask(id) {
  const sql = `UPDATE t_task_job SET end_date=DATE_SUB(CURDATE(), INTERVAL 1 DAY), status='close' WHERE id=${id}`;
  return executeSql(sql);
}

/**
 * 创建AppKey
 * @param {Object} appKeyData - 包含login_name和password的对象
 * @returns {Promise<Object>} 返回创建结果
 */
export async function createAppKey(appKeyData) {
  const { login_name, password } = appKeyData;
  const sql = `INSERT INTO t_sys_user (login_name, password) VALUES ('${login_name}', '${password}')`;
  return executeSql(sql);
}

/**
 * 删除AppKey
 * @param {number} id - AppKey ID
 * @returns {Promise<Object>} 返回删除结果
 */
export async function deleteAppKey(id) {
  const sql = `DELETE FROM t_sys_user WHERE id=${id}`;
  return executeSql(sql);
}

/**
 * 创建转发API
 * @param {Object} apiData - 包含label, title, method, type, content的对象
 * @returns {Promise<Object>} 返回创建结果
 */
export async function createForwardApi(apiData) {
  const { label, title, method, type, content } = apiData;

  const sql = `INSERT INTO t_api_config (label, title, method, type, content) VALUES ('${label}', '${title}', '${method}', '${type}', '${escapeContent(
    content,
  )}')`;
  return executeSql(sql);
}

/**
 * 更新转发API
 * @param {number} id - API ID
 * @param {Object} apiData - 包含label, title, method, content的对象
 * @returns {Promise<Object>} 返回更新结果
 */
export async function updateForwardApi(id, apiData) {
  const { label, title, method, content } = apiData;
  const sql = `UPDATE t_api_config SET label='${label}', title='${title}', method='${method}', content='${escapeContent(
    content,
  )}' WHERE id=${id}`;
  return executeSql(sql);
}

/**
 * 删除转发API
 * @param {number} id - API ID
 * @returns {Promise<Object>} 返回删除结果
 */
export async function deleteForwardApi(id) {
  const sql = `DELETE FROM t_api_config WHERE id=${id}`;
  return executeSql(sql);
}
/**
 * 获取安全验证列表
 * @param {number} id - API ID
 * @returns {Promise<Object>} 返回列表结果
 */
export async function getDoubleVerificationList(domain) {
  if (!domain) {
    domain = "";
  }
  const sql = `select id,domain,call_back,access_key,access_key_expire_time from morefun_oa.sys_domain where domain like ('%${domain}%')`;
  return executeSql(sql);
}
/**
 * 获取陶店长清洗订单列表
 * @param {string} title - 搜索标题
 * @returns {Promise<Object>} 返回列表
 */
export async function getTdzCleaningOrderListByTitle(title) {
  const currentUserName = (await getCurrentUser())?.username;
  let queryCurrentUser = ``;
  if (currentUserName != "liang") {
    //查询所有即可
    queryCurrentUser = `and (add_user = '${currentUserName}' or add_user is null or add_user = '')`;
  }
  const sql = `select * from tdz_order_clean_record where title like ('%${title}%') ${queryCurrentUser} order by id desc limit 30`;
  return executeSql(sql);
}

/**
 * 添加陶店长清洗订单列表
 * @param {number}  domain- API ID
 * @returns {Promise<Object>} 返回结果
 */
export async function saveTdzCleaningOrder(domain) {
  const currentUserName = (await getCurrentUser())?.username;
  if (!domain) {
    return;
  }
  const cleanChannel = domain.cleanChannel || "TB";
  const sql = `insert into tdz_order_clean_record (title,business_id,business_code,business_value,start_day,end_day,\`sql\`,add_user,clean_channel) values ('${domain.title}','${domain.businessId}','${domain.businessCode}','','${domain.startDay}','${domain.endDay}',"${domain.sql}","${currentUserName}","${cleanChannel}")`;
  console.log("saveTdzCleaningOrder sql:", sql);
  return executeSql(sql);
}
async function getCurrentUser() {
  const user = JSON.parse(localStorage.getItem("auth-user") || "null");
  return user;
}
